import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, User, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import surinameFlag from '../assets/images/Flag_of_Suriname.png';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.instructions'), path: '/instructions' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.faq'), path: '/faq' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-primary-600 shadow-lg relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Brand */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src={surinameFlag} 
              alt="Suriname" 
              className="h-8 w-12 object-cover rounded shadow-md"
            />
            <div className="text-white">
              <h1 className="text-xl font-bold">Sranslate™</h1>
              <p className="text-xs text-primary-200">BETA VERSION</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`nav-link ${isActive(item.path) ? 'nav-link-active' : ''}`}
              >
                {item.name}
              </Link>
            ))}
            <a
              href="mailto:info@sranslate.nl"
              className="nav-link flex items-center space-x-1"
            >
              <Mail size={16} />
              <span>{t('nav.contact')}</span>
            </a>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-white hover:text-primary-200 focus:outline-none focus:text-primary-200"
                >
                  <User size={20} />
                  <span className="text-sm">{user?.name || 'User'}</span>
                </button>
                
                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b">
                      <div className="font-medium">{user?.name}</div>
                      <div className="text-gray-500">{user?.email}</div>
                    </div>
                    <button
                      onClick={() => {
                        logout();
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <LogOut size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-white text-primary-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-50 transition-colors"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-primary-200 focus:outline-none focus:text-primary-200"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-primary-600 border-t border-primary-500 shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`block px-3 py-2 text-white hover:text-primary-200 hover:bg-primary-700 rounded-md ${
                  isActive(item.path) ? 'bg-primary-700 text-primary-200' : ''
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <a
              href="mailto:info@sranslate.nl"
              className="block px-3 py-2 text-white hover:text-primary-200 hover:bg-primary-700 rounded-md flex items-center space-x-2"
              onClick={() => setIsMenuOpen(false)}
            >
              <Mail size={16} />
              <span>{t('nav.contact')}</span>
            </a>

            {/* Mobile Language Switcher */}
            <div className="px-3 py-2">
              <LanguageSwitcher />
            </div>
            
            {/* Mobile User Menu */}
            {isAuthenticated ? (
              <>
                <div className="px-3 py-2 text-primary-200 border-t border-primary-500 mt-2">
                  <div className="flex items-center space-x-2">
                    <User size={16} />
                    <span className="text-sm">{user?.name}</span>
                  </div>
                  <div className="text-xs text-primary-300">{user?.email}</div>
                </div>
                <button
                  onClick={() => {
                    logout();
                    setIsMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-white hover:text-primary-200 hover:bg-primary-700 rounded-md flex items-center space-x-2"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block px-3 py-2 bg-white text-primary-600 rounded-md text-center font-medium hover:bg-primary-50 mx-3 mt-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;